export default function ContentSections() {
  return (
    <>
      {/* What Is Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="lg:w-1/2">
              <span className="inline-block px-4 py-1.5 text-xs font-medium text-accent-700 bg-accent-100/70 rounded-full mb-4">
                About Superbuy
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                What Is a Superbuy Spreadsheet?
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                A Superbuy Spreadsheet is an organized collection of products, seller information, prices, and shopping resources that helps buyers purchase products more efficiently. Think of it as your personal shopping assistant in spreadsheet form — curated, organized, and ready to use.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                Instead of spending hours manually searching through countless product pages, you can browse a curated collection vetted by experienced shoppers. The best superbuy spreadsheet resources include detailed notes about product quality, sizing accuracy, and seller reliability.
              </p>
              <ul className="space-y-3">
                {[
                  { icon: "ri-flashlight-line", text: "Faster product discovery and shopping" },
                  { icon: "ri-scales-3-line", text: "Better price comparison across sellers" },
                  { icon: "ri-shield-check-line", text: "Trusted seller discovery and verification" },
                  { icon: "ri-zoom-in-line", text: "Easier quality control with detailed notes" },
                  { icon: "ri-ship-line", text: "Shipping optimization with cost comparison" },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-accent-600 mt-0.5 flex-shrink-0">
                      <i className={`${item.icon} text-sm`}></i>
                    </span>
                    <span className="text-sm text-foreground-700">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:w-1/2 w-full">
              <div className="w-full h-[300px] md:h-[380px] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Close%20up%20photography%20of%20hands%20organizing%20colorful%20sticky%20notes%20and%20printed%20product%20photos%20on%20a%20large%20whiteboard%2C%20creative%20planning%20session%20aesthetic%2C%20bright%20overhead%20office%20lighting%2C%20organized%20chaos%20concept%2C%20editorial%20documentary%20style%2C%20warm%20wood%20desk%20visible%20at%20bottom&width=800&height=700&seq=what-is-v2&orientation=portrait"
                  alt="Superbuy Spreadsheet organized on a laptop"
                  title="Superbuy Spreadsheet Organization"
                  className="w-full h-full object-cover object-top rounded-xl"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Table */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
            Why Buyers Use <span className="text-accent-600">Superbuy Spreadsheets</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {[
              { icon: "ri-timer-line", title: "Time Saving", desc: "Quickly locate products with direct links and organized categories, eliminating repetitive searching." },
              { icon: "ri-folder-5-line", title: "Organization", desc: "Categories and subcategories make browsing intuitive, with all product links neatly structured." },
              { icon: "ri-money-dollar-circle-line", title: "Price Comparison", desc: "Multiple sellers listed side by side with updated pricing for effortless comparison." },
              { icon: "ri-search-eye-line", title: "QC Research", desc: "Community quality control notes help you make better-informed purchasing decisions." },
              { icon: "ri-speed-up-line", title: "Shopping Efficiency", desc: "Process orders faster with pre-vetted product links and organized seller information." },
              { icon: "ri-user-heart-line", title: "Trusted Community", desc: "Access community-vetted recommendations built into every quality superbuy spreadsheet." },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 md:p-6">
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/80 text-accent-600 mb-4">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-3">
            How Superbuy <span className="text-accent-600">Works</span>
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-10 max-w-xl mx-auto">
            A simple six-step process from product discovery to receiving your package
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { step: "01", icon: "ri-search-line", title: "Find Products", desc: "Browse your spreadsheet for items" },
              { step: "02", icon: "ri-links-line", title: "Add Links", desc: "Copy product links to your agent" },
              { step: "03", icon: "ri-shopping-cart-2-line", title: "Purchase", desc: "Order through shopping agent" },
              { step: "04", icon: "ri-camera-line", title: "QC Photos", desc: "Review warehouse quality photos" },
              { step: "05", icon: "ri-ship-2-line", title: "Select Shipping", desc: "Choose best shipping method" },
              { step: "06", icon: "ri-home-4-line", title: "Receive", desc: "Package arrives at your door" },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-3 rounded-full bg-accent-100/80 flex items-center justify-center">
                  <span className="w-6 h-6 flex items-center justify-center text-accent-600">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                </div>
                <span className="text-xs font-semibold text-accent-500 bg-accent-100/80 px-2 py-0.5 rounded-full">{item.step}</span>
                <h4 className="text-sm font-semibold text-foreground-950 mt-2 mb-1">{item.title}</h4>
                <p className="text-xs text-foreground-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shipping Comparison */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
            Shipping <span className="text-accent-600">Comparison</span>
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70">
                  <th className="text-left py-3 px-4 font-semibold text-foreground-800 whitespace-nowrap">Shipping Line</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-800 whitespace-nowrap">Speed</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-800 whitespace-nowrap">Cost</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground-800 whitespace-nowrap">Reliability</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { line: "EMS", speed: "Medium (7-15 days)", cost: "Medium", reliability: "High", reliabilityClass: "text-accent-600" },
                  { line: "DHL Express", speed: "Fast (3-7 days)", cost: "High", reliability: "Very High", reliabilityClass: "text-accent-600" },
                  { line: "Air Cargo", speed: "Slow (15-30 days)", cost: "Low", reliability: "Medium", reliabilityClass: "text-secondary-600" },
                  { line: "Sea Freight", speed: "Very Slow (30-60 days)", cost: "Very Low", reliability: "Medium", reliabilityClass: "text-secondary-600" },
                  { line: "EUB", speed: "Slow-Medium (10-25 days)", cost: "Low", reliability: "Medium-High", reliabilityClass: "text-accent-600" },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-background-200/50 hover:bg-background-50 transition-colors">
                    <td className="py-3 px-4 text-foreground-800 font-medium whitespace-nowrap">{row.line}</td>
                    <td className="py-3 px-4 text-foreground-600 whitespace-nowrap">{row.speed}</td>
                    <td className="py-3 px-4 text-foreground-600 whitespace-nowrap">{row.cost}</td>
                    <td className={`py-3 px-4 font-medium whitespace-nowrap ${row.reliabilityClass}`}>{row.reliability}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* QC & Safety */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                QC <span className="text-accent-600">Guide</span>
              </h2>
              <p className="text-sm text-foreground-500 mb-5 leading-relaxed">
                Quality Control is essential before shipping your products. Follow these steps to ensure you receive exactly what you ordered.
              </p>
              <ul className="space-y-3">
                {[
                  { icon: "ri-checkbox-circle-line", text: "Check stitching quality on all garments" },
                  { icon: "ri-ruler-line", text: "Verify sizing against measurement charts" },
                  { icon: "ri-image-line", text: "Examine QC photos from multiple angles" },
                  { icon: "ri-contrast-2-line", text: "Compare items against reference images" },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-accent-600 mt-0.5 flex-shrink-0">
                      <i className={`${item.icon} text-sm`}></i>
                    </span>
                    <span className="text-sm text-foreground-700">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                Safety <span className="text-accent-600">Tips</span>
              </h2>
              <p className="text-sm text-foreground-500 mb-5 leading-relaxed">
                Protect yourself and your purchases with these essential safety practices when using any superbuy spreadsheet.
              </p>
              <ul className="space-y-3">
                {[
                  { icon: "ri-shield-check-line", text: "Verify sellers before placing large orders" },
                  { icon: "ri-lock-line", text: "Use secure payment methods only" },
                  { icon: "ri-file-search-line", text: "Review QC photos carefully before shipping" },
                  { icon: "ri-fingerprint-line", text: "Protect your personal data at all times" },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-5 h-5 flex items-center justify-center text-accent-600 mt-0.5 flex-shrink-0">
                      <i className={`${item.icon} text-sm`}></i>
                    </span>
                    <span className="text-sm text-foreground-700">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}